import { ISearchingTag, TagName, TagType } from "src/app/shared/components/tag-searching-input/~types/models/SearchingTag";
import { ConvertFromFilterToChannelType, ConvertFromFilterToOperatorType, ConvertFromFilterToChatType, ConvertFromFilterToStatus } from "src/app/shared/helpers/chats-filter-helper/ChatsFilterHelper";
import { ChatType } from "../../chats-panel/~store/models/enums/ChatType"
import { OperatorType } from "../../operator/~store/models/enums/OperatorType"
import { ChannelType } from "../../channel/~store/models/enums/ChannelType"
import { ChatStatus } from "../../chats/~store/models/Chat"
import { ConvertFromFilterToUTCTimeInterval, ParseUTCTimeInterval } from "src/app/shared/helpers/date-helper/FromFilterToDateHelper";
import moment from "moment";
import i18n from "src/app/shared/localization/i18n";

const getFilterValue = (tag: ISearchingTag) => {
    if (tag.externalValue !== undefined) return tag.externalValue;

    switch (tag.tagName) {
        case TagName.Channel: return ConvertFromFilterToChannelType(tag.value);
        case TagName.OperatorType: return ConvertFromFilterToOperatorType(tag.value);
        case TagName.ChatType: return ConvertFromFilterToChatType(tag.value);
        case TagName.Date: return ConvertFromFilterToUTCTimeInterval(tag.value);
        case TagName.Status: return ConvertFromFilterToStatus(tag.value);
        case TagName.ChatNumber: return String(tag.value ?? "").replace(/\s+/g, "").replace(/\u00A0/g, "");
        default: return tag.value;
    }
};

const DateUnits: Record<string, moment.unitOfTime.StartOf> = {
    [i18n.t('common.today')]: 'day',
    [i18n.t('common.currentWeek')]: 'isoWeek',
    [i18n.t('common.currentMonth')]: 'month',
    [i18n.t('common.currentYear')]: 'year'
};

const TagOperatorEnum = {
    NotEquals: 0,
    Equals: 1
};

export const isChatMatchingClientFilters = (chat: any, tags: ISearchingTag[]): boolean => {
    if (!tags?.length) return true;

    const groupedFilters = tags.reduce((acc, tag, index) => {
        if (tag.tagType !== TagType.ClosingTag) return acc;

        let operator = TagOperatorEnum.Equals; 
        const prevTag = tags[index - 1];

        if (prevTag && prevTag.tagType === TagType.OperationTag && prevTag.value === "!=") {
            operator = TagOperatorEnum.NotEquals;
        }

        const key = tag.tagName!;
        if (!acc[key]) acc[key] = [];
        acc[key].push({ tag, operator });
        return acc;
    }, {} as Record<string, { tag: ISearchingTag, operator: number }[]>); 

    for (const key in groupedFilters) {
        const tagName = Number(key) as TagName;
        const filters = groupedFilters[key];

        if (filters.length === 0) continue;

        const firstOperator = filters[0].operator;
        const isOrLogic = tagName === TagName.ChannelName && firstOperator === TagOperatorEnum.Equals;

        const isGroupMatch = filters[isOrLogic ? 'some' : 'every']((filterItem) => {
            const { tag, operator } = filterItem;
            const isEquals = operator === TagOperatorEnum.Equals;

            const systemValue = getFilterValue(tag);
            const filterValue = String(systemValue).toLowerCase();

            let match = false;

            if (tagName === TagName.Date) {
                const chatTime = moment(chat.timeCreated);
                if (tag.value === i18n.t('common.yesterday')) {
                    match = chatTime.isSame(moment().subtract(1, 'days'), 'day');
                }
                else if (DateUnits[tag.value]) {
                    match = chatTime.isSame(new Date(), DateUnits[tag.value]);
                }
                else {
                    const range = ParseUTCTimeInterval(String(systemValue));
                    if (range) {
                        const chatTs = chatTime.valueOf();
                        match = chatTs >= range.leftDate.valueOf() && chatTs < range.rightDate.valueOf();
                    }
                }
            }
            else if (tagName === TagName.OperatorType) {
                if (chat.operator === null) {
                    match = (filterValue === "queue");
                }
                else if (filterValue === "human") {
                    match = (chat.operator.type !== OperatorType.Bot);
                }
                else {
                    const typeStr = (OperatorType[chat.operator.type] || "").toLowerCase();
                    match = (typeStr === filterValue);
                }
            }
            else {
                let chatRawValue: any = '';
                switch (tagName) {
                    case TagName.ChatNumber: chatRawValue = chat.chatId; break;
                    case TagName.Contact: chatRawValue = chat.contactId; break;
                    case TagName.ChannelName: chatRawValue = chat.channelId; break;
                    case TagName.Operator: chatRawValue = chat.operatorId; break;
                    case TagName.Channel: chatRawValue = ChannelType[chat.channelType]; break;
                    case TagName.ChatType: chatRawValue = ChatType[chat.chatType]; break;
                    case TagName.Status: chatRawValue = chat.chatStats ? ChatStatus[chat.chatStats.status] : ''; break;
                    default: return false;
                }
                const chatValue = String(chatRawValue || '').toLowerCase();
                match = (chatValue === filterValue);
            }
            return isEquals ? match : !match;
        });

        if (!isGroupMatch) return false;
    }
    return true;
};